import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataQualityAPI } from "../../api/DataQualityAPI";
import {
  FIELD_LABELS, LIST_FIELDS, confidenceLabel, formatValue, groupByEvent, hostOf,
} from "./proposalFormat";
import { Change } from "./ChangeView";
import DuplicateCard from "./DuplicateCard";
import "../dashboard/components/Marker-UI.css";
import "./DataQuality.css";
import PageTitle from '../../components/PageTitle';

// Corrections a data-quality check found in the event data. Nothing changes until the admin
// accepts a proposal here; an accept writes that one field to the live event, and the backend
// refuses it when the field changed since the check.

const CONFIDENCE_FILTERS = [
  { id: "ALL", label: "Alle" },
  { id: "HIGH", label: "Sicher" },
  { id: "MEDIUM", label: "Wahrscheinlich" },
  { id: "LOW", label: "Unsicher" },
];

export const errorText = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 409 && message) return message;
  if (status === 404) return "Nicht gefunden: der Vorschlag oder das Event existiert nicht mehr.";
  if (status === 401 || status === 403) return "Keine Berechtigung – bitte neu anmelden.";
  return message || "Speichern fehlgeschlagen – bitte noch einmal versuchen.";
};

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

function ProposalRow({ p, state, onAccept, onReject }) {
  const busy = state?.status === "saving";
  const decided = state?.status === "ACCEPTED" || state?.status === "REJECTED";
  const blocked = p.outdated || p.eventMissing;

  if (decided) {
    return (
      <li className={`dq-row dq-row--decided dq-row--${state.status.toLowerCase()}`}>
        <span className="dq-field">{FIELD_LABELS[p.field] ?? p.field}</span>
        <span className="dq-decided">
          {state.status === "ACCEPTED" ? "Übernommen" : "Abgelehnt"}
          {state.status === "ACCEPTED" && <span className="dq-decided-value"> · {LIST_FIELDS.has(p.field) ? "neue Liste" : formatValue(p.field, p.newValue)}</span>}
        </span>
      </li>
    );
  }

  return (
    <li className={`dq-row${blocked ? " dq-row--blocked" : ""}`}>
      <div className="dq-row-body">
      <div className="dq-row-head">
        <span className="dq-field">{FIELD_LABELS[p.field] ?? p.field}</span>
        <span className={`dq-badge dq-badge--${String(p.confidence).toLowerCase()}`}>{confidenceLabel(p.confidence)}</span>
      </div>

      <Change p={p} />

      <p className="dq-reason">{p.reason}</p>

      {p.sources?.length > 0 && (
        <div className="dq-sources">
          {p.sources.map((s) => (
            <a key={s} href={s} target="_blank" rel="noreferrer" className="dq-source" title={s}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" aria-hidden="true">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              {hostOf(s)}
            </a>
          ))}
        </div>
      )}

      {p.outdated && (
        <p className="dq-warning" role="note">
          Der Wert wurde seit der Prüfung geändert. Aktuell: <strong>{LIST_FIELDS.has(p.field) ? "andere Liste" : formatValue(p.field, p.currentValue)}</strong>. Übernehmen ist gesperrt.
        </p>
      )}
      {p.eventMissing && <p className="dq-warning" role="note">Das Event gibt es nicht mehr (archiviert oder gelöscht).</p>}
      {state?.error && <p className="dq-error" role="alert">{state.error}</p>}
      </div>

      <div className="dq-actions">
        <button type="button" className="dq-btn dq-btn--reject" onClick={onReject} disabled={busy}>
          Ablehnen
        </button>
        <button type="button" className="dq-btn dq-btn--accept" onClick={onAccept} disabled={busy || blocked}>
          {busy ? <span className="dq-spinner" aria-hidden="true" /> : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
          Übernehmen
        </button>
      </div>
    </li>
  );
}

const STATUS_LABELS = { ACCEPTED: "Übernommen", REJECTED: "Abgelehnt", OBSOLETE: "Erledigt" };

function DecidedList({ rows }) {
  if (!rows.length) return <div className="dq-empty"><p>Noch keine Entscheidungen.</p></div>;
  return (
    <ul className="dq-history">
      {rows.map((p) => (
        <li key={p.id} className="dq-history-row">
          <span className={`dq-status dq-status--${String(p.status).toLowerCase()}`}>{STATUS_LABELS[p.status] ?? p.status}</span>
          <span className="dq-history-event">{p.markerTitle} <span className="dq-muted">· {formatValue("eventDate", p.eventDate)}</span></span>
          <span className="dq-history-field">{p.kind === "DUPLICATE" ? "Duplikat" : FIELD_LABELS[p.field] ?? p.field}</span>
          <span className="dq-history-change">
            {p.kind === "DUPLICATE"
              ? (p.status === "ACCEPTED" ? `entfernt, ID ${p.markerId} · bleibt ID ${p.relatedMarkerId}` : `ID ${p.markerId} und ${p.relatedMarkerId} bleiben`)
              : LIST_FIELDS.has(p.field) ? "Liste geändert" : `${formatValue(p.field, p.oldValue)} → ${formatValue(p.field, p.newValue)}`}
          </span>
          <span className="dq-muted dq-history-when">{fmtDateTime(p.decidedAt)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function DataQuality() {
  const [view, setView] = useState("PENDING");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [states, setStates] = useState({}); // proposal id → { status, error }
  const [confidence, setConfidence] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await DataQualityAPI.list(view === "PENDING" ? "PENDING" : "DECIDED"));
      setStates({});
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const patch = (id, next) => setStates((prev) => ({ ...prev, [id]: next }));

  const decide = useCallback(async (p, action, options) => {
    patch(p.id, { status: "saving" });
    try {
      const res = await (action === "accept" ? DataQualityAPI.accept(p.id, options) : DataQualityAPI.reject(p.id));
      // A removed duplicate names the entry that is gone, which "keep the other" swaps.
      patch(p.id, { status: action === "accept" ? "ACCEPTED" : "REJECTED", removedId: res?.markerId, removedTitle: res?.markerTitle });
      return true;
    } catch (e) {
      patch(p.id, { status: "open", error: errorText(e) });
      return false;
    }
  }, []);

  const isOpen = (p) => !["ACCEPTED", "REJECTED"].includes(states[p.id]?.status);
  const acceptable = (p) => isOpen(p) && !p.outdated && !p.eventMissing && states[p.id]?.status !== "saving";

  const acceptAll = async (items) => {
    for (const p of items.filter(acceptable)) {
      // One after the other: each accept rereads the event the previous one changed.
      // eslint-disable-next-line no-await-in-loop
      await decide(p, "accept");
    }
  };

  const visible = useMemo(
    () => (confidence === "ALL" ? rows : rows.filter((p) => p.confidence === confidence)),
    [rows, confidence],
  );
  const duplicates = useMemo(() => visible.filter((p) => p.kind === "DUPLICATE"), [visible]);
  const groups = useMemo(() => groupByEvent(visible.filter((p) => p.kind !== "DUPLICATE")), [visible]);
  const openCount = rows.filter(isOpen).length;
  // Removing an event is never done in bulk.
  const sureOpen = rows.filter((p) => p.kind !== "DUPLICATE" && p.confidence === "HIGH" && acceptable(p));
  const allOpen = rows.filter((p) => p.kind !== "DUPLICATE" && acceptable(p));
  // Unlike the sure ones, this takes guesses too (e.g. the default age 18), so it asks once.
  const acceptEverything = () => {
    const unsure = allOpen.filter((p) => p.confidence !== "HIGH").length;
    const question = `${allOpen.length} Vorschläge übernehmen, davon ${unsure} wahrscheinlich oder unsicher? Duplikate bleiben offen.`;
    if (window.confirm(question)) acceptAll(allOpen);
  };
  const countFor = (id) => (id === "ALL" ? rows.length : rows.filter((p) => p.confidence === id).length);

  return (
    <div className="page dq-page">
      <div className="page-header">
        <PageTitle title="Datenqualität" description="Korrekturvorschläge aus dem automatischen Check, z. B. eine andere Uhrzeit oder ein anderer Preis. Übernehmen oder verwerfen." />
        <div className="dq-header-actions">
          {view === "PENDING" && sureOpen.length > 0 && (
            <button type="button" className="btn-add btn-add--green" onClick={() => acceptAll(sureOpen)}>
              <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Alle sicheren übernehmen ({sureOpen.length})
            </button>
          )}
          {view === "PENDING" && allOpen.length > sureOpen.length && (
            <button type="button" className="btn-add btn-add--white" onClick={acceptEverything}>
              <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 7 17l-5-5" />
                <path d="m22 10-7.5 7.5L13 16" />
              </svg>
              Alle übernehmen ({allOpen.length})
            </button>
          )}
          <button type="button" className="btn-add btn-add--white" onClick={load} aria-label="Aktualisieren">
            <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1 0 9-9" />
              <path d="M3 3v6h6" />
            </svg>
            Aktualisieren
          </button>
        </div>
      </div>

      <div className="dq-toolbar">
        <div className="dq-segment" role="tablist" aria-label="Ansicht">
          <button type="button" role="tab" aria-selected={view === "PENDING"} className={view === "PENDING" ? "active" : ""} onClick={() => setView("PENDING")}>
            Offen{view === "PENDING" && !loading ? ` · ${openCount}` : ""}
          </button>
          <button type="button" role="tab" aria-selected={view === "DECIDED"} className={view === "DECIDED" ? "active" : ""} onClick={() => setView("DECIDED")}>
            Entschieden
          </button>
        </div>
        {view === "PENDING" && rows.length > 0 && (
          <div className="dq-filters" aria-label="Nach Sicherheit filtern">
            {CONFIDENCE_FILTERS.map((f) => (
              <button key={f.id} type="button" className={`dq-filter${confidence === f.id ? " active" : ""}`} onClick={() => setConfidence(f.id)}>
                {f.label} <span className="dq-filter-count">{countFor(f.id)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner" role="status" aria-label="Lädt Vorschläge" />
          <span className="muted">Lade Vorschläge…</span>
        </div>
      )}

      {error && !loading && (
        <div className="error-state" role="alert">
          <span>{error}</span>
          <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 12 }} onClick={load}>Erneut laden</button>
        </div>
      )}

      {!loading && !error && view === "DECIDED" && <DecidedList rows={rows} />}

      {!loading && !error && view === "PENDING" && duplicates.length > 0 && (
        <div className="dq-dup-section">
          <h2 className="dq-section-title">Mögliche Duplikate <span className="dq-filter-count">{duplicates.filter(isOpen).length}</span></h2>
          {duplicates.map((p) => (
            <DuplicateCard
              key={p.id}
              p={p}
              state={states[p.id]}
              onRemove={() => decide(p, "accept")}
              onKeepOther={() => decide(p, "accept", { keepOther: true })}
              onReject={() => decide(p, "reject")}
            />
          ))}
        </div>
      )}

      {!loading && !error && view === "PENDING" && groups.length > 0 && duplicates.length > 0 && (
        <h2 className="dq-section-title">Korrekturen</h2>
      )}

      {!loading && !error && view === "PENDING" && groups.length === 0 && duplicates.length === 0 && (
        <div className="dq-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="30" height="30" aria-hidden="true">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="M22 4 12 14.01l-3-3" />
          </svg>
          <p>Alles geprüft – keine offenen Vorschläge.</p>
          <span className="dq-muted">Neue Vorschläge kommen aus dem Event-Check (Claude, Connector „schick-prod“).</span>
        </div>
      )}

      {!loading && !error && view === "PENDING" && groups.length > 0 && (
        <div className="dq-groups">
          {groups.map((g) => {
            const open = g.items.filter(isOpen);
            const ready = g.items.filter(acceptable);
            return (
              <section key={g.markerId} className="dq-event" aria-label={g.title}>
                <header className="dq-event-head">
                  <div className="dq-event-meta">
                    <span className="dq-event-date">{formatValue("eventDate", g.eventDate)}</span>
                    <h2 className="dq-event-title">{g.title}</h2>
                    <span className="dq-muted">ID {g.markerId} · {open.length} offen</span>
                  </div>
                  {ready.length > 1 && (
                    <button type="button" className="dq-btn dq-btn--ghost" onClick={() => acceptAll(g.items)}>
                      Alle übernehmen ({ready.length})
                    </button>
                  )}
                </header>
                <ul className="dq-rows">
                  {g.items.map((p) => (
                    <ProposalRow
                      key={p.id}
                      p={p}
                      state={states[p.id]}
                      onAccept={() => decide(p, "accept")}
                      onReject={() => decide(p, "reject")}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
